import {
  Maybe,
  Product,
  ProductLifeCycleState,
} from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'
import { DigitalDataOrigin, ProductWithCategoryType } from './digital-data-types'
import {
  GA4Product,
  GA4CartProduct,
  GA4ItemCategories,
} from '@dustin-web/microsite-components/src/utils/digital-data-types'
import { getMissingCartDetails } from './cart-local-storage'
import { findNullProperties } from '@dustin-web/microsite-config/src/utils'
import { newRelicLogger } from '@dustin-web/microsite-config/src/newrelic/logger'
import {
  getGA4ProductMandatoryFields,
  getGA4ProductSkippableFields,
} from './digital-data-mappers-mandatory-fields'

type ItemKey = `item_category${number}`

export const mapProductDataLegacy = (
  product: ProductWithCategoryType,
  category: string | undefined,
  translate: (key: string, opt?: object) => string
) => {
  const details = product.availability?.availabilityDetails
  const availabilityKey = details?.key?.replace(/_/g, '')
  const availabilityId = `AvailabilityText_${availabilityKey ?? ''}`

  const etaDate = product.availability?.showEtaDate && details?.formattedDateUtc

  const productLifeCycleStateValue =
    product.availability?.productLifeCycleState ?? ProductLifeCycleState.None
  const productLifeCycleState = Object.keys(ProductLifeCycleState).find(
    state => (ProductLifeCycleState as any)[state] === productLifeCycleStateValue
  )

  const variant_count = product.variants?.length || undefined

  const returnObj = {
    Availability: availabilityKey && translate(availabilityId, { date: etaDate }),
    Category: category,
    EtaDate: etaDate,
    HasImage: !!product.primaryImageId,
    Manufacturer: product.manufacturerName,
    Price: product.price?.priceExcludingVat?.toFixed(2),
    ProductId: product.productErpIdentifier,
    ProductLifeCycle: productLifeCycleState,
    ProductName: `${product.manufacturerName} ${product.displayName} ${product.displaySpecifications}`,
    WeightedAverageScore: null,
    Quantity: product.quantity,
    has_variants: false,
    label: product.label,
  }

  if (variant_count) {
    return { ...returnObj, variant_count, has_variants: true }
  }

  return returnObj
}

type ProductData =
  | ProductWithCategoryType
  | (Product & {
      productListIndex?: number
      label?: string[]
      pageDisplayData?: Record<string, unknown> | null
    })

export const mapProductData = (
  product: ProductData,
  origin?: DigitalDataOrigin,
  eventName?: string,
  extraFields?: any
): GA4Product => {
  const productLifeCycleStateValue =
    product.availability?.productLifeCycleState ?? ProductLifeCycleState.None

  // because product can be a ProductWithCategoryType or a Product
  let variant_count: Maybe<number> | undefined = undefined
  let productQuantity: Maybe<number> | undefined = undefined
  let productLabel: string[] | null | undefined = undefined
  let productCategoryPathEng: string | null | undefined = undefined

  if (product.variants) {
    variant_count = product.variants?.variantsInformation?.length
    productQuantity = (product as Product).lotQuantity
  }

  if ('quantity' in product) {
    productQuantity = product.quantity ?? 1
  }

  if ('categoryPathEng' in product) {
    productCategoryPathEng = product.categoryPathEng
  }

  productLabel = product?.label ?? ['no_label_badge']

  const categoryPaths =
    productCategoryPathEng?.split('/') || product.category?.englishPathSlug?.split('/') || []

  const availabilityKey = product.availability?.availabilityDetails?.key || 'unknown'
  const descriptionIsAiGenerated = product?.pageDisplayData?.descriptionIsAiGenerated

  const path = new URL(window.location.href).pathname
  const lastParam = path.substring(path.lastIndexOf('/') + 1)
  const itemListPage = lastParam === '' ? '/' : lastParam

  const returnObj: GA4Product = {
    ...pathToObjectNotation(categoryPaths),
    ...extraFields,
    item_name:
      product.englishFullDisplayName ??
      `${product.manufacturerName} ${product.displayName} ${product.displaySpecifications}`,
    item_id: product.productErpIdentifier,
    price: product.price?.priceExcludingVat?.toFixed(2) || '',
    item_brand: product.manufacturerName,
    item_list_name: origin,
    item_list_page: itemListPage,
    index: product.productListIndex !== undefined ? product.productListIndex + 1 : undefined,
    hasImage: product.primaryImageId ? true : false,
    quantity: productQuantity,
    availability: availabilityKey,
    product_life_cycle: productLifeCycleStateValue,
    weighted_average_score: product.reviewScore?.toString().split('.').join('_'),
    has_variants: variant_count ? true : false,
    variant_count: variant_count ?? 0,
    label: productLabel,
    ...(typeof descriptionIsAiGenerated === 'boolean' && {
      has_ai_description: descriptionIsAiGenerated,
    }),
  }

  const skipFields = getGA4ProductSkippableFields(eventName)
  const mandatoryFields = getGA4ProductMandatoryFields(eventName)
  const nullProperties = findNullProperties(returnObj, mandatoryFields, skipFields)
  if (nullProperties.length > 0) {
    newRelicLogger.error(
      {
        origin,
        productId: product.productErpIdentifier,
        nullProperties,
        hasNullProperties: true,
        eventName,
      },
      'GA4Product has null properties'
    )
  }

  return returnObj
}

export const mapCartProductData = (product: ProductWithCategoryType): GA4CartProduct => {
  const categoryPaths = product.categoryPathEng?.split('/') || []

  const productLifeCycleStateValue =
    product.availability?.productLifeCycleState ?? ProductLifeCycleState.None

  const details = product.availability?.availabilityDetails
  const etaDate = product.availability?.showEtaDate && details?.formattedDateUtc

  const missingCartDetails = getMissingCartDetails()
  const productDetails = missingCartDetails[product.productErpIdentifier]

  return {
    ...pathToObjectNotation(categoryPaths),
    item_name:
      product.englishFullDisplayName ??
      `${product.manufacturerName} ${product.displayName} ${product.displaySpecifications}`,
    item_id: product.productErpIdentifier,
    price: parseFloat(product.price?.priceExcludingVat?.toFixed(2) ?? '0'),
    item_brand: product.manufacturerName,
    quantity: product.quantity ?? 1,
    item_list_name: productDetails?.item_list_name.slice(-(product.quantity || 1)) || [
      'listUndefined',
    ],
    item_list_page: productDetails?.item_list_page.slice(-(product.quantity || 1)) || [
      'listUndefined',
    ],
    availability:
      product.availability?.availabilityDetails?.key || product.availability?.availabilityStatus,

    HasImage: !!product.primaryImageId,
    EtaDate: etaDate,
    WeightedAverageScore: null,
    ProductLifeCycle: productLifeCycleStateValue,
    has_variants: false,
    label: product.label,
  } as GA4CartProduct
}

const pathToObjectNotation = (path: string[]) =>
  path.reduce((acc, val, index) => {
    if (index > 9) {
      return acc
    }

    if (index === 0) {
      acc['item_category'] = val
      return acc
    }

    const key: ItemKey = `item_category${index + 1}`
    acc[key] = val

    return acc
  }, {} as GA4ItemCategories)
